import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin, Filter } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { LocationSearchInput } from "./location/LocationSearchInput";
import { Button } from "./ui/button";

export function MobileHero({ 
  showFilterButton = false, 
  onFilterClick,
  showSearchInput = true,
}: { 
  showFilterButton?: boolean;
  onFilterClick?: () => void;
  showSearchInput?: boolean;
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  
  // Initialize from URL parameters if available
  const initialLocation = searchParams.get('location') || "";
  const [locationInput, setLocationInput] = useState(initialLocation);
  
  // Extract coordinates from URL parameters
  const lat = searchParams.get('lat');
  const lng = searchParams.get('lng');
  const initialCoordinates = (lat && lng) 
    ? { lat: parseFloat(lat), lng: parseFloat(lng) } 
    : null;
  
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(initialCoordinates);

  const handlePlaceSelected = (place: { formattedAddress: string; coordinates: { lat: number; lng: number } }) => {
    setLocationInput(place.formattedAddress);
    setCoordinates(place.coordinates);
    
    // Immediately update the search parameters
    const newParams = new URLSearchParams(searchParams);
    newParams.set('location', place.formattedAddress);
    newParams.set('lat', place.coordinates.lat.toString());
    newParams.set('lng', place.coordinates.lng.toString());
    setSearchParams(newParams, { replace: true });
  };

  const handleSearch = () => {
    if (!locationInput) {
      toast({
        title: 'Error',
        description: 'Please enter a location',
        variant: 'destructive',
      });
      return;
    }

    if (!coordinates) {
      toast({
        title: 'Error',
        description: 'Please select a valid location from the suggestions',
        variant: 'destructive'
      });
      return;
    }

    // If we're not already on the search page, navigate there
    if (!window.location.pathname.includes('/search')) {
      const params = new URLSearchParams(searchParams);
      params.set("location", locationInput);
      params.set("lat", coordinates.lat.toString());
      params.set("lng", coordinates.lng.toString());
      navigate(`/search?${params.toString()}`);
    }
  };

  return (
    <div className="bg-[#f4f2f2] pt-8 pb-6">
      {/* Main Header */}
      <div className="px-4 mb-5">
        <div className="w-full mx-auto text-center">
          <div className="text-center mb-4">
            <h1 className="text-3xl leading-tight font-medium text-brand-primary w-full">
              Your Cities Best Florists <span className="emoji inline-block"><span className="animate-spin-slow inline-block" style={{ fontFamily: "sans-serif", WebkitAnimation: "spin 3s linear infinite", animation: "spin 3s linear infinite", display: "inline-block", transformOrigin: "center" }}>🌸</span></span>
            </h1>
            
            <h2 className="text-3xl leading-tight text-brand-primary font-medium mt-2 w-full">
              Ready to Checkout Now <span className="emoji">🛒</span>
            </h2>
          </div>
          
          <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-sm text-[#4A4F41]">
            <span className="flex items-center">
              <span className="w-1.5 h-1.5 rounded-full bg-[#967F81] mr-1.5"></span>
              Handpicked Florists
            </span>
            <span className="flex items-center">
              <span className="w-1.5 h-1.5 rounded-full bg-[#967F81] mr-1.5"></span>
              Same-Day Options
            </span>
            <span className="flex items-center">
              <span className="w-1.5 h-1.5 rounded-full bg-[#967F81] mr-1.5"></span>
              Date & Time Filtering
            </span>
            <span className="flex items-center">
              <span className="w-1.5 h-1.5 rounded-full bg-[#967F81] mr-1.5"></span>
              Secure Checkout
            </span>
            <span className="flex items-center">
              <span className="w-1.5 h-1.5 rounded-full bg-[#967F81] mr-1.5"></span>
              Order Updates
            </span>
          </div>
        </div>
      </div>

      {/* Search Input Section - Now conditional */}
      {showSearchInput && (
      <div className="px-4 mt-1">
        <div className="w-full mx-auto">
          {/* Search Row with Location and Filter */}
          <div className="flex items-center gap-1.5">
            {/* Address Input */}
            <div className="flex-1 flex items-center gap-1 border border-brand-primary/20 rounded-lg pl-2 pr-1 py-2 bg-white overflow-hidden">
              <MapPin className="h-3.5 w-3.5 text-brand-primary flex-shrink-0" />
              <LocationSearchInput
                defaultValue={locationInput}
                onPlaceSelected={handlePlaceSelected}
                placeholder="Enter your delivery address..."
                className="bg-transparent border-0 text-[#4A4F41] placeholder:text-[#4A4F41]/40 placeholder:text-xs text-[12px] md:text-[14px] p-0 m-0 focus:ring-0 w-full min-w-0 flex-1"
              />
            </div>
            
            {/* Filter Button */}
            {showFilterButton && (
              <Button
                onClick={onFilterClick}
                variant="outline"
                className="h-10 w-10 p-0 border border-brand-primary/20 text-brand-secondary bg-[#967F81] hover:bg-[#843329]/20 rounded-lg"
              >
                <Filter className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
} 